/**
 * IMPORTS
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MessageCircleQuestion, Undo2 } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { SelectPagination } from "@components/select-pagination/select-pagination";
import { Spinner } from "@components/spinner/spinner";
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsList, TabsTrigger } from "@components/tabs";

import { ObservacaoModal } from "@pages/admin/pesquisar-remessas/components/observacao-modal";
import { SearchInputShipment } from "@pages/admin/pesquisar-remessas/components/search-item";

// hooks
import { useManageSearchShipment } from "@/hooks/search-shipment/use-search-shipment";

// zustand
import { useStoreZustandSearchShipment } from "@/store-zustand/search-shipment/search-shipment";

import { DevolverEnvioDadosModal } from "@/app/pages/admin/pesquisar-remessas/components/return-shipment-modal";
import { FEATURES, useFeature } from "@/utils/auth/features";

const SearchShipment = () => {
  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    isLoading,
    itemsPerPage,
    isLoadingPage,
    setItemsPerPage,
    lastPage,
    remessa,
    setRemessa,
  } = useStoreZustandSearchShipment();

  const navigate = useNavigate();

  const [isModal, setIsModal] = useState(false);
  const [isModalDevolver, setIsModalDevolver] = useState(false);
  const [remessaDevolver, setRemessaDevolver] = useState<{
    id: number;
    numero_remessa: string;
  } | null>(null);

  const podeDevolverRemessa = useFeature(FEATURES.DEVOLVER_REMESSAS);

  const { searchShipments, paginations, isLoadingMain } = useManageSearchShipment();

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  // função para lidar com a alteração do seletor de itens por página
  const handleItemsPerPageChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      setItemsPerPage(Number(event.target.value));
    },
    [setItemsPerPage]
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleToogleModal = useCallback(() => {
    setIsModal(!isModal);
  }, [isModal]);

  const handleCloseModalDevolver = useCallback(() => {
    setIsModalDevolver(false);
    setRemessaDevolver(null);
  }, []);

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <Tabs defaultValue="all">
          <div className="mb-4 flex items-center">
            <TabsList>
              <TabsTrigger value="all">Remessas</TabsTrigger>
            </TabsList>

            <div className="ml-auto flex items-center gap-2">
              <div className="hidden md:block">
                <SelectPagination
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
              <Button
                size="sm"
                variant="default"
                className="h-8 gap-1 text-neutral-50"
                onClick={handleGoBack}
              >
                <Undo2 className="h-3.5 w-3.5" />
                <span className="sm:whitespace-nowra sr-only text-neutral-50 sm:not-sr-only">
                  Voltar
                </span>
              </Button>
            </div>
          </div>
          <DynamicTable
            title="Pesquisar remessas"
            description="Gerencie suas remessas e visualize quando quiser."
            isLoadingPage={isLoadingMain ? isLoadingMain : isLoadingPage}
            data={searchShipments ?? []}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItemsPage}
            lastPage={lastPage}
            onGoToPage={onGoToPage}
            paginations={paginations}
            actions={[
              {
                icon: Undo2,
                label: "Devolver para envio de dados",
                iconClassName: "h-4 w-4 text-orange-600",
                visible: (item: { situacao?: string }) =>
                  podeDevolverRemessa && item.situacao === "conferido",
                onClick: (item: { id: number; numero_remessa: string }) => {
                  setRemessaDevolver({
                    id: item?.id,
                    numero_remessa: item?.numero_remessa,
                  });
                  setIsModalDevolver(true);
                },
              },
              {
                icon: MessageCircleQuestion,
                label: "Observação",
                iconClassName: (item: any) =>
                  item.hasMensagem ? "h-4 w-4 text-yellow-500" : "h-4 w-4 text-zinc-900",
                onClick: (item: any) => {
                  setRemessa(item);
                  setIsModal(true);
                },
              },
            ]}
            columns={[
              { label: "Remessa", accessor: "numero_remessa" },
              { label: "Cliente", accessor: "nome", limit: 60 },
              {
                label: "Situação",
                accessor: "status",
                isBadge: true,
              },
              {
                label: "Qtd",
                accessor: "quantidade",
                className: "hidden md:table-cell",
              },
              { label: "Modelo", accessor: "modelo", limit: 60 },
              { label: "Consultor", accessor: "consultor", limit: 60 },
              { label: "Responsável", accessor: "responsavel", limit: 60 },
              { label: "Solicitante", accessor: "solicitante", limit: 60 },
              { label: "Data Solicitacao", accessor: "data" },
              { label: "Data Finalização", accessor: "data_fim_producao" },
            ]}
            childerSearch={<SearchInputShipment />}
          />
        </Tabs>
      )}

      {isModal && remessa && <ObservacaoModal handleToggleCreateActvityModal={handleToogleModal} />}

      {isModalDevolver && remessaDevolver && (
        <DevolverEnvioDadosModal
          id_remessa={remessaDevolver.id}
          numero_remessa={remessaDevolver.numero_remessa}
          onClose={handleCloseModalDevolver}
        />
      )}
    </>
  );
};

/**
 * EXPORTS
 */
export { SearchShipment };
